import { AxiosResponse } from 'axios';
import { UserTeamsPayload } from 'components/usersTable/types';
import { invalidEmailError } from 'constants/invites';
import { ACCOUNT_STATUS } from 'contexts/accounts/types';
import { UserTeam } from 'contexts/teams/types';
import { findAssignedSkillDive } from 'hooks/useSkillDiveToggleState';
import { AlreadyInviteds, getAlreadyInvited } from 'services/alreadyInviteds';

export type InviteWithTeams = {
  email?: string;
  is_account_owner?: boolean;
  is_manager?: boolean;
  has_license?: boolean;
  has_skill_dive?: boolean;
  userTeams?: UserTeam[];
  teams?: UserTeamsPayload[];
};

// The fields batch_email_invite declares for each invite entry, plus `teams`.
type BatchInviteField = 'email' | 'is_account_owner' | 'is_manager' | 'has_license';

export type BatchInviteEntry<T extends InviteWithTeams> = Pick<T, BatchInviteField> & {
  has_skill_dive?: boolean;
  teams: UserTeamsPayload[];
};

interface BuildInvitesOptions {
  /** Sends `has_skill_dive`; off keeps the request exactly as it was before the field existed. */
  withSkillDive?: boolean;
}

// Skill Dive needs a training license, so an invite without one never keeps Skill Dive.
export const applySkillDiveDependency = <T extends InviteWithTeams>(invites: T[]): T[] =>
  invites.map((invite) =>
    invite.has_skill_dive && !invite.has_license ? { ...invite, has_skill_dive: false } : invite
  );

// Builds each entry from the declared fields only, so UI state on an invite (`userTeams`, `id`,
// `team`, ...) never reaches the request.
export const buildInvitesWithTeams = <T extends InviteWithTeams>(
  invites: T[],
  { withSkillDive = false }: BuildInvitesOptions = {}
): BatchInviteEntry<T>[] =>
  invites.map((invite) => ({
    email: invite.email,
    is_account_owner: invite.is_account_owner,
    is_manager: invite.is_manager,
    has_license: invite.has_license,
    ...(withSkillDive && {
      has_skill_dive: Boolean(invite.has_license && invite.has_skill_dive),
    }),
    // An empty or missing selection sends no teams, clearing any stale `teams`.
    teams: (invite.userTeams || []).map((userTeam) => ({
      team_id: userTeam.team?.id || userTeam.team?.team_id || userTeam.team_id,
      is_manager: userTeam.is_manager,
    })),
  }));

// An access-step row: an invitee, or an existing user with their current access.
interface SeatTaker {
  has_license?: boolean;
  has_skill_dive?: boolean;
  existing?: { has_license: boolean; license_addons: { id: string; addon_name?: string }[] };
}

export interface SeatCount {
  /** Seats rows take that they don't hold yet. */
  gained: number;
  /** Seats existing users give up. */
  released: number;
}

const countSeats = (
  rows: SeatTaker[],
  wants: (row: SeatTaker) => boolean,
  holds: (existing: SeatTaker['existing']) => boolean
): SeatCount =>
  rows.reduce(
    (count, row) => {
      const held = Boolean(row.existing && holds(row.existing));
      if (wants(row) && !held) count.gained += 1;
      if (!wants(row) && held) count.released += 1;
      return count;
    },
    { gained: 0, released: 0 }
  );

export const licenseSeats = (rows: SeatTaker[]): SeatCount =>
  countSeats(
    rows,
    (row) => Boolean(row.has_license),
    (existing) => existing.has_license
  );

// Saving leaves a seat alone when the user has no training license before or after, so it isn't
// counted as given up.
export const keepsSkillDiveSeat = ({ has_skill_dive, has_license, existing }: SeatTaker): boolean =>
  Boolean(has_skill_dive) ||
  Boolean(existing && findAssignedSkillDive(existing) && !has_license && !existing.has_license);

export const skillDiveSeats = (rows: SeatTaker[]): SeatCount =>
  countSeats(rows, keepsSkillDiveSeat, (existing) => Boolean(findAssignedSkillDive(existing)));

// Seats the rows take overall. A seat an existing user gives up is free for another row, since
// those changes are sent before anything that takes a seat.
export const takenLicenses = (rows: SeatTaker[]): number => {
  const { gained, released } = licenseSeats(rows);
  return gained - released;
};

export const takenSkillDiveSeats = (rows: SeatTaker[]): number => {
  const { gained, released } = skillDiveSeats(rows);
  return gained - released;
};

// An email that's already in use, as the already-invited check returns it.
export interface AlreadyInvitedEntry {
  email: string;
  account?: { id: string; status?: string };
  is_active: boolean;
  teams: { name: string }[];
}

// Why an email that's already in use can't be invited.
export const alreadyInvitedReason = (
  invitation: AlreadyInvitedEntry,
  accountId: string
): string => {
  if (invitation.account?.id !== accountId) {
    return invitation.account?.status === ACCOUNT_STATUS.EXPIRED
      ? 'Failed: This user belongs to an expired account. The account owner must remove them first before they can be added here.'
      : 'Failed: This email address is associated with another account, check if the email is already associated with an account';
  }
  const teamNames = invitation.teams.map((team) => team.name).join(', ');
  if (invitation.is_active) {
    return invitation.teams.length > 0
      ? `Failed: This email address is already associated to teams ${teamNames}`
      : 'Failed: This email address is already associated with this account';
  }
  return invitation.teams.length > 0
    ? `Failed: This email address has already been invited to teams ${teamNames}`
    : 'Failed: This email address has already been invited checks if you have another invite active';
};

export interface EmailCheck {
  /** Emails the server refused to check, with why. */
  rejected: { email: string; error: string }[];
  alreadyInvited: AlreadyInvitedEntry[];
}

// The server refuses the whole check over an email it won't accept, naming it by its position in
// the list, so the rest are checked again without it.
export const checkAlreadyInvited = async (emails: string[]): Promise<EmailCheck> => {
  let rejected: EmailCheck['rejected'] = [];
  let response: AxiosResponse<AlreadyInviteds> | undefined;
  try {
    response = await getAlreadyInvited(emails);
  } catch (error) {
    const byPosition: Record<string, string[]> = error?.response?.data?.details?.emails;
    if (!byPosition || typeof byPosition !== 'object') throw error;
    rejected = Object.entries(byPosition).map(([index, messages]) => ({
      email: emails[Number(index)],
      error: invalidEmailError(messages?.[0]),
    }));
    const rest = emails.filter((email) => !rejected.some((entry) => entry.email === email));
    if (rest.length) response = await getAlreadyInvited(rest);
  }
  // The response has `teams`, though the service type says `team`.
  const alreadyInvited = (response?.data.already_invited ?? []) as unknown as AlreadyInvitedEntry[];
  return { rejected, alreadyInvited };
};

// A refused license or team change: an error, or `result: false` whatever the status.
export const isRefusal = (response: unknown): boolean =>
  response instanceof Error || (response as { result?: boolean } | null)?.result === false;

// The account a modal was opened with, overlaid with the session's live copy of it, which carries
// the counts refetched after a refusal.
export const withLiveAccount = <T extends { id?: string }>(
  openedWith: T,
  sessionAccount?: { id?: string } | null
): T =>
  sessionAccount?.id && sessionAccount.id === openedWith?.id
    ? ({ ...openedWith, ...sessionAccount } as T)
    : openedWith;
